import React, { useState, useEffect } from 'react';

const CheckIcon = (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
        <path d="M2 6l3 3 5-7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const CloseIcon = (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
        <path d="M3 3l6 6M9 3l-6 6" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    </svg>
);

const labelStyle = {
    fontSize: 10.5,
    fontWeight: 600,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: 'var(--ink-3, #6c6a68)',
};

const ghostBtnStyle = {
    appearance: 'none',
    height: 30,
    padding: '0 12px',
    background: 'transparent',
    border: '1px solid var(--line, #e7e3da)',
    borderRadius: 7,
    font: 'inherit',
    fontSize: 12.5,
    fontWeight: 500,
    color: 'var(--ink-2, #3a3835)',
    cursor: 'pointer',
};

const primaryBtnStyle = (disabled) => ({
    appearance: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    height: 30,
    padding: '0 14px',
    background: disabled ? 'var(--ink-4, #b2afa9)' : 'var(--ink, #0e0e10)',
    border: 0,
    borderRadius: 7,
    font: 'inherit',
    fontSize: 12.5,
    fontWeight: 600,
    color: 'var(--bg, #fbfaf7)',
    cursor: disabled ? 'not-allowed' : 'pointer',
});

const ContextSyncModal = ({
    open,
    onClose,
    matches,
    newTranslation,
    onReplaceSelected,
}) => {
    const [selectedIndices, setSelectedIndices] = useState([]);

    useEffect(() => {
        if (open && matches) setSelectedIndices(matches.map((m) => m.index));
    }, [open, matches]);

    useEffect(() => {
        if (!open) return;
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, onClose]);

    if (!open) return null;

    const toggle = (index) => {
        setSelectedIndices((prev) =>
            prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
        );
    };

    const allSelected = matches && selectedIndices.length === matches.length;
    const toggleAll = () => {
        if (allSelected) setSelectedIndices([]);
        else setSelectedIndices(matches.map((m) => m.index));
    };

    const confirm = () => {
        onReplaceSelected(selectedIndices);
        onClose();
    };

    return (
        <div
            className="rs-app"
            role="dialog"
            aria-modal="true"
            aria-labelledby="rs-csm-title"
            onClick={onClose}
            style={{
                position: 'fixed',
                inset: 0,
                zIndex: 2000,
                background: 'rgba(14,14,16,0.42)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 24,
                fontFamily: "Inter, system-ui, -apple-system, sans-serif",
                animation: 'rsCsmFade 140ms ease-out',
            }}
        >
            <style>{`
                @keyframes rsCsmFade { from { opacity: 0 } to { opacity: 1 } }
                @keyframes rsCsmPop  { from { opacity: 0; transform: translateY(6px) scale(0.99) } to { opacity: 1; transform: none } }
                .rs-csm-row:hover { background: var(--bg-soft, #f5f1ea); }
                .rs-csm-check {
                    appearance: none;
                    width: 16px; height: 16px;
                    border: 1.5px solid var(--line-2, #d8d2c5);
                    border-radius: 4px;
                    background: var(--bg, #fbfaf7);
                    cursor: pointer;
                    display: inline-grid; place-items: center;
                    flex-shrink: 0;
                    transition: background 120ms ease, border-color 120ms ease;
                }
                .rs-csm-check:checked {
                    background: var(--ink, #0e0e10);
                    border-color: var(--ink, #0e0e10);
                }
                .rs-csm-check:checked::after {
                    content: '';
                    width: 8px; height: 5px;
                    border-left: 1.6px solid var(--bg, #fff);
                    border-bottom: 1.6px solid var(--bg, #fff);
                    transform: rotate(-45deg) translate(1px, -1px);
                }
            `}</style>

            <div
                onClick={(e) => e.stopPropagation()}
                style={{
                    width: 'min(720px, 100%)',
                    maxHeight: '85vh',
                    background: 'var(--bg-card, #ffffff)',
                    border: '1px solid var(--line, #e7e3da)',
                    borderRadius: 12,
                    boxShadow: '0 24px 64px rgba(14,14,16,0.18), 0 4px 12px rgba(14,14,16,0.06)',
                    display: 'flex',
                    flexDirection: 'column',
                    color: 'var(--ink, #0e0e10)',
                    overflow: 'hidden',
                    animation: 'rsCsmPop 180ms cubic-bezier(0.2, 0.8, 0.2, 1)',
                }}
            >
                {/* Header */}
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        padding: '14px 18px',
                        borderBottom: '1px solid var(--line, #e7e3da)',
                    }}
                >
                    <div style={{ flexGrow: 1, minWidth: 0 }}>
                        <div style={{ ...labelStyle, marginBottom: 2 }}>Smart replace</div>
                        <div id="rs-csm-title" style={{ fontSize: 15, fontWeight: 600 }}>
                            Review matches
                        </div>
                    </div>
                    <span style={{ fontSize: 12, color: 'var(--ink-3, #6c6a68)' }}>
                        {selectedIndices.length} of {matches?.length || 0} selected
                    </span>
                    <button
                        type="button"
                        onClick={toggleAll}
                        style={{ ...ghostBtnStyle, height: 26, fontSize: 11.5, padding: '0 9px' }}
                    >
                        {allSelected ? 'Deselect all' : 'Select all'}
                    </button>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close"
                        style={{
                            appearance: 'none',
                            display: 'inline-grid',
                            placeItems: 'center',
                            width: 28,
                            height: 28,
                            background: 'transparent',
                            border: 0,
                            borderRadius: 6,
                            color: 'var(--ink-3, #6c6a68)',
                            cursor: 'pointer',
                        }}
                    >
                        {CloseIcon}
                    </button>
                </div>

                {/* New translation banner */}
                <div
                    style={{
                        padding: '14px 18px',
                        background: 'var(--accent-soft, #fbe8de)',
                        borderBottom: '1px solid var(--line, #e7e3da)',
                    }}
                >
                    <div style={{ ...labelStyle, color: 'var(--accent-2, #b85a3e)', marginBottom: 4 }}>
                        New translation to apply
                    </div>
                    <div style={{ fontSize: 13.5, lineHeight: 1.5, color: 'var(--ink, #0e0e10)', fontWeight: 500 }}>
                        {newTranslation}
                    </div>
                </div>

                {/* Matches list */}
                <div style={{ overflowY: 'auto', flex: 1 }}>
                    {(matches || []).map((match) => {
                        const checked = selectedIndices.includes(match.index);
                        return (
                            <label
                                key={match.index}
                                htmlFor={`rs-csm-${match.index}`}
                                className="rs-csm-row"
                                style={{
                                    display: 'grid',
                                    gridTemplateColumns: '24px 1fr',
                                    gap: 12,
                                    padding: '14px 18px',
                                    borderBottom: '1px solid var(--line, #e7e3da)',
                                    cursor: 'pointer',
                                    transition: 'background 120ms ease',
                                }}
                            >
                                <input
                                    id={`rs-csm-${match.index}`}
                                    type="checkbox"
                                    className="rs-csm-check"
                                    checked={checked}
                                    onChange={() => toggle(match.index)}
                                    style={{ marginTop: 2 }}
                                />
                                <div style={{ minWidth: 0 }}>
                                    <div
                                        style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            padding: '2px 7px',
                                            borderRadius: 5,
                                            background: 'var(--bg-soft, #f5f1ea)',
                                            color: 'var(--ink-2, #3a3835)',
                                            fontSize: 10.5,
                                            fontWeight: 600,
                                            letterSpacing: '0.02em',
                                            marginBottom: 6,
                                        }}
                                    >
                                        Segment {match.index + 1}
                                    </div>
                                    <div
                                        style={{
                                            fontSize: 13,
                                            lineHeight: 1.5,
                                            color: 'var(--ink, #0e0e10)',
                                            fontWeight: 500,
                                            marginBottom: 6,
                                        }}
                                    >
                                        {match.source}
                                    </div>
                                    <div style={{ ...labelStyle, marginBottom: 2 }}>Current target</div>
                                    <div
                                        style={{
                                            fontSize: 12.5,
                                            lineHeight: 1.5,
                                            color: match.currentTranslation ? 'var(--ink-2, #3a3835)' : 'var(--ink-4, #b2afa9)',
                                            fontStyle: match.currentTranslation ? 'normal' : 'italic',
                                        }}
                                    >
                                        {match.currentTranslation || 'No translation yet'}
                                    </div>
                                </div>
                            </label>
                        );
                    })}
                </div>

                {/* Footer */}
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        padding: '12px 18px',
                        borderTop: '1px solid var(--line, #e7e3da)',
                        background: 'var(--bg, #fbfaf7)',
                    }}
                >
                    <span style={{ fontSize: 11.5, color: 'var(--ink-3, #6c6a68)' }}>
                        Selected translations will replace existing targets in each segment.
                    </span>
                    <span style={{ flexGrow: 1 }} />
                    <button type="button" onClick={onClose} style={ghostBtnStyle}>
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={confirm}
                        disabled={selectedIndices.length === 0}
                        style={primaryBtnStyle(selectedIndices.length === 0)}
                    >
                        {CheckIcon}
                        Replace {selectedIndices.length || ''}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ContextSyncModal;
